<!--
  Full-area splash loader: bouncing moin logo with a thin indeterminate bar.

  It is `position: absolute; inset: 0`, so it fills the nearest POSITIONED
  ancestor — give the element you drop it into `relative` and a height, or its
  white backdrop climbs the tree and blanks out whatever it lands on (the app
  shell's SidebarInset, app bar included). Use `fixed` when covering the whole
  viewport is what you actually want.
-->
<script setup lang="ts">
import logoUrl from '@/assets/moin-logo-mini.svg';

withDefaults(
  defineProps<{
    /** Cover the whole viewport instead of the nearest positioned ancestor. */
    fixed?: boolean;
  }>(),
  { fixed: false },
);
</script>

<template>
  <div class="hub-loader" :class="[fixed && 'hub-loader--fixed']">
    <div style="margin-top: -3%">
      <div style="width: 36px">
        <img :src="logoUrl" class="logo-bounce-in" alt="" />
      </div>
      <div class="hub-loader-bar mt-2" />
    </div>
  </div>
</template>

<style scoped>
.hub-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
}

.hub-loader--fixed {
  position: fixed;
  z-index: 9999;
}

/* Thin indeterminate progress bar (replaces Vuetify's v-progress-linear). */
.hub-loader-bar {
  position: relative;
  overflow: hidden;
  border-radius: 3px;
  height: 2px;
  width: 36px;
  /* --secondary is a full oklch color, so mix rather than hsl-wrap it. */
  background: color-mix(in oklab, var(--secondary) 20%, transparent);
}

.hub-loader-bar::after {
  content: '';
  position: absolute;
  inset: 0;
  width: 40%;
  background: var(--secondary);
  animation: hub-loader-indeterminate 1.1s ease-in-out infinite;
}

@keyframes hub-loader-indeterminate {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  60% {
    transform: scale(1.2);
  }
  80% {
    transform: scale(0.8);
  }
  100% {
    transform: scale(1);
  }
}

.logo-bounce-in {
  animation: bounce-in 0.6s ease-in-out;
}
</style>
